import {
  Box,
  Button,
  Divider,
  FormControl,
  FormHelperText,
  OutlinedInput,
  Typography,
} from '@mui/material'
import { useState, useEffect } from 'react'
import { isValidEmail, sanitizeEmail, sendSignInLinkWithOTP } from '../lib/auth'
import OTPVerification from './OTPVerification'
import SSOButtons from './SSOButtons'
import { CircularProgress } from '@mui/material'

enum FormStatus {
  Error = 'error',
  Idle = 'idle',
  Sending = 'sending',
  Sent = 'sent',
  Loading = 'loading',
}

interface EmailLinkFormProps {
  isOpen: boolean
  emailAutosentTo: string
}

export default function EmailLinkForm({
  isOpen,
  emailAutosentTo: emailAutosentTo,
}: EmailLinkFormProps) {
  const [emailAutosent, setEmailAutosent] = useState(!!emailAutosentTo)
  const [email, setEmail] = useState(emailAutosentTo || '')
  const [status, setStatus] = useState<FormStatus>(FormStatus.Loading)

  useEffect(() => {
    if (!isOpen) return

    const params = new URLSearchParams(window.location.search)
    const emailParam = params.get('email')

    if (!emailParam) {
      setStatus(FormStatus.Idle)
      return
    }

    const sanitized = sanitizeEmail(emailParam)
    setStatus(FormStatus.Loading)
    setEmail(sanitized)
    submitEmail(sanitized)
    setEmailAutosent(true)
  }, [isOpen])

  function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    setEmail(sanitizeEmail(event.target.value))
  }

  async function submitEmail(email: string) {
    try {
      const success = await sendSignInLinkWithOTP(email)

      if (success) {
        setStatus(FormStatus.Sent)
      } else {
        setStatus(FormStatus.Error)
      }

      return success
    } catch (err) {
      console.error('Error sending email otp:', err)
      setStatus(FormStatus.Error)
      return false
    }
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    if (!isValidEmail(email)) return false

    setStatus(FormStatus.Sending)

    await submitEmail(email)
  }

  function handleTryAgain() {
    setEmail('')
    setStatus(FormStatus.Idle)
  }

  if (status === FormStatus.Loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 200,
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  if (status === FormStatus.Sent || emailAutosent) {
    return (
      <OTPVerification
        onClick={handleTryAgain}
        email={email}
        emailAutosent={emailAutosent}
      />
    )
  }

  return (
    <Box>
      <Box>
        <Typography
          gutterBottom
          variant='h1'
          sx={{
            textAlign: 'center',
          }}
        >
          Welcome Back!
        </Typography>
        <Typography
          variant='subtitle1'
          sx={{
            textAlign: 'center',
          }}
        >
          To access your dashboard, sign in below:
        </Typography>
      </Box>
      <form
        onSubmit={handleSubmit}
        style={{ width: '100%', marginTop: '1.5rem', marginBottom: '1.5rem' }}
      >
        <FormControl fullWidth error={status === FormStatus.Error}>
          <Typography variant='body2'>Email Address</Typography>
          <OutlinedInput
            type='email'
            name='email'
            value={email}
            onChange={handleChange}
            placeholder='Enter your email'
            sx={{ mb: 1 }}
          />
          {status === FormStatus.Error && (
            <FormHelperText sx={{ color: 'error.main' }}>
              Failed to send email. Try again.
            </FormHelperText>
          )}
        </FormControl>

        <Button
          id='get-email-sign-in-link'
          variant='contained'
          color='secondary'
          fullWidth
          size='large'
          type='submit'
          sx={{
            height: '48px',
            mt: 2,
            '&.Mui-disabled': {
              color: 'primary.contrastText',
            },
          }}
          disabled={!isValidEmail(email) || status === FormStatus.Sending}
        >
          {status === FormStatus.Sending
            ? 'Sending...'
            : 'Verify my email address'}
        </Button>
      </form>
      <Divider>or</Divider>
      <Box
        sx={{
          mt: 3,
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <SSOButtons />
      </Box>
    </Box>
  )
}
